/* eslint-disable react/prop-types */
/**
 * WellsTab — three-level hierarchy: wells -> well_tracts -> regulatory.
 *
 * Same master/detail pattern as LeaseDeedsTab, but with two cascading
 * "Active X" AntD Selects (Active Well + Active Well Tract) because the
 * extra level makes a single row click insufficient.
 *
 * The library `WigMetadataCrudPanel` keeps row-click → edit modal across
 * all three grids; the selectors drive which parent the sub-grids belong
 * to. Changing the active well clears tracts + regulatory below.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Empty, Segmented, Select, Space, Typography } from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import {
  applyFieldEditability,
  stripNonWritableFields,
} from "../../../utils/applyFieldEditability";
import {
  filterBrokerHiddenFields,
  isExternalBrokerAgent,
} from "../../../utils/brokerHiddenFields";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import ReadOnlyChildTable from "./ReadOnlyChildTable";
import AttachmentsScopeButton from "./AttachmentsScopeButton";
import { fanOutChildren } from "./dealWideChildren";
import { numericCellRenderers } from "../../../utils/metadataV2Columns";
import { countyFieldRenderer } from "../../../components/countySelect";
import { TRS_FIELD_RENDERERS } from "../../../components/trsSelect";
import {
  INTEREST_FIELD_RENDERERS,
  withShareBoundsNotice,
} from "../../../components/interestInput";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";
import { filterLookupOptions } from "../../../utils/lookupFilter";

const { Text } = Typography;

// Strip parent-driven fields. The library panel has no `hideFields` prop,
// so pre-filtering metadata is the supported way to suppress columns.
const HIDDEN_WELL_FIELDS = new Set(["deal_id"]);
// dry_hole_check_nbr / completion_check_nbr were retired by de-hub migration
// 072 (redundant with the Payments Check # field). Migrations run out-of-band,
// so listing them here keeps the tract form from rendering inputs the update
// model already forbids — which would 422 every tract edit until the migration
// lands. Safe to drop once every environment has run 072.
const HIDDEN_TRACT_FIELDS = new Set([
  "well_id",
  "dry_hole_check_nbr",
  "completion_check_nbr",
]);
const HIDDEN_REGULATORY_FIELDS = new Set(["well_tract_id"]);

const wellLabel = (w) => {
  if (!w) return "";
  const name = w.well_name || `Well #${w.id}`;
  const api = w.api_10 ? ` — API ${w.api_10}` : "";
  return `${name}${api}`;
};

// Operator display label. Names are NOT unique (de-hub migration 065 — the
// upstream master holds eight names under two codes each), so the code is
// appended when the row carries one. Shared by the `operator_id` typeahead,
// the inline create path, and the wells-grid cell renderer so an operator
// reads the same everywhere.
const operatorLabel = (o) => {
  if (!o) return "";
  const name = o.name || `Operator ${o.id}`;
  return o.operator_code ? `${name} (${o.operator_code})` : name;
};

const tractLabel = (t) => {
  if (!t) return "";
  const loc = t.location_type ? `${t.location_type} ` : "";
  return `${loc}Tract #${t.id}`;
};

const WellsTab = ({ dealId }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // /wells, /well_tracts and /well_tract_regulatory are
  // [BD.BrokerAgent, BD.Landman, BD.LandTech] for POST/DELETE, which
  // `record.edit` mirrors exactly. PUT /wells is one role wider —
  // BD.DivisionOrders is admitted to stamp `well_integrated_date`, the column
  // it exclusively owns — so the update gate is computed from the metadata
  // below rather than from `record.edit`.
  const perms = usePermissions();
  const { can } = perms;
  const mayEditRecords = can("record.edit");
  // An EXTERNAL broker agent — same test as the deals list and LeaseDeedsTab,
  // so a user holding BrokerAgent alongside an internal role keeps the
  // internal view.
  const isBrokerAgent = isExternalBrokerAgent(perms);

  const [wells, setWells] = useState([]);
  const [wellMeta, setWellMeta] = useState([]);
  const [tracts, setTracts] = useState([]);
  const [tractMeta, setTractMeta] = useState([]);
  const [regulatory, setRegulatory] = useState([]);
  const [regulatoryMeta, setRegulatoryMeta] = useState([]);

  // operator_id -> display label, for the wells grid. The column stores an FK
  // id and the library grid renders the raw stored value, so without this map
  // the Operator cell shows a bare number. Mirrors EntitiesTab's entity_id
  // map (UAT #33).
  const [operatorNames, setOperatorNames] = useState({});

  const [selectedWellId, setSelectedWellId] = useState(null);
  const [selectedTractId, setSelectedTractId] = useState(null);

  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingTracts, setLoadingTracts] = useState(false);
  const [loadingRegulatory, setLoadingRegulatory] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  // Scope toggle for the Well Tracts grid: "selected" = today's editable
  // per-well tracts (+ Active Well Tract selector + regulatory); "deal" =
  // read-only roll-up of every well tract across the deal. Regulatory is
  // per-tract only, so it's hidden in "deal" mode. Deal-wide data is fetched
  // lazily (only when the toggle flips to "deal").
  const [childScope, setChildScope] = useState("selected");
  const [dealWellTracts, setDealWellTracts] = useState([]);
  const [dealRegulatory, setDealRegulatory] = useState([]);
  const [loadingDealWide, setLoadingDealWide] = useState(false);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refreshWells = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoadingTop(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, wMetaResp, tMetaResp, rMetaResp, opResp] =
        await Promise.all([
          dealsService.listWells({ token: tk, userOid, dealId }),
          dealsService.getMetadata({ token: tk, userOid, tableName: "wells" }),
          dealsService.getMetadata({
            token: tk,
            userOid,
            tableName: "well_tracts",
          }),
          dealsService.getMetadata({
            token: tk,
            userOid,
            tableName: "well_tract_regulatory",
          }),
          // Eager-load every operator — including deactivated ones, so a well
          // still pointing at one resolves — purely to label the grid's
          // `operator_id` column. Non-fatal: on failure the cell falls back to
          // the raw id rather than failing the whole tab load.
          dealsService
            .listAllOperators({ token: tk, userOid, includeInactive: true })
            .catch(() => null),
        ]);
      const rows = listResp?.data?.data || [];
      setWells(rows);
      setWellMeta(wMetaResp?.data?.data || []);
      setTractMeta(tMetaResp?.data?.data || []);
      setRegulatoryMeta(rMetaResp?.data?.data || []);
      const opRows = opResp?.data?.data || opResp?.data || [];
      const opLabels = {};
      for (const o of Array.isArray(opRows) ? opRows : []) {
        if (o?.id != null) opLabels[o.id] = operatorLabel(o);
      }
      setOperatorNames(opLabels);
      setSelectedWellId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      console.error("WellsTab top load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingTop(false);
    }
  }, [dealId, getAuth]);

  const refreshTracts = useCallback(async () => {
    if (!selectedWellId) {
      setTracts([]);
      setSelectedTractId(null);
      return;
    }
    try {
      setLoadingTracts(true);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listWellTracts({
        token: tk,
        userOid,
        wellId: selectedWellId,
      });
      const rows = resp?.data?.data || [];
      setTracts(rows);
      setSelectedTractId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      console.error("WellsTab tracts load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingTracts(false);
    }
  }, [selectedWellId, getAuth]);

  const refreshRegulatory = useCallback(async () => {
    if (!selectedTractId) {
      setRegulatory([]);
      return;
    }
    try {
      setLoadingRegulatory(true);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listWellTractRegulatory({
        token: tk,
        userOid,
        wellTractId: selectedTractId,
      });
      setRegulatory(resp?.data?.data || []);
    } catch (err) {
      console.error("WellsTab regulatory load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingRegulatory(false);
    }
  }, [selectedTractId, getAuth]);

  // Deal-wide roll-up: fan out over ALL wells (already loaded in state) to
  // fetch every well tract, tagging each row with its well label. Read-only —
  // editing stays in "selected" mode.
  const refreshDealWide = useCallback(async () => {
    try {
      setLoadingDealWide(true);
      const { token: tk, userOid } = await getAuth();
      const rows = await fanOutChildren({
        masters: wells,
        fetchChild: (w) =>
          dealsService.listWellTracts({ token: tk, userOid, wellId: w.id }),
        labelOf: wellLabel,
      });
      setDealWellTracts(rows);

      // Regulatory hangs off the TRACTS, not the wells, so it needs the rows
      // above before it can be fetched — hence a second pass rather than a
      // parallel one. Labelled by tract, because "which tract is this order
      // on" is the question the whole-deal view is being asked.
      const reg = await fanOutChildren({
        masters: rows,
        fetchChild: (tr) =>
          dealsService.listWellTractRegulatory({
            token: tk,
            userOid,
            wellTractId: tr.id,
          }),
        labelOf: tractLabel,
      });
      setDealRegulatory(reg);
    } catch (err) {
      console.error("WellsTab deal-wide load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoadingDealWide(false);
    }
  }, [wells, getAuth]);

  useEffect(() => {
    refreshWells();
  }, [refreshWells]);

  useEffect(() => {
    refreshTracts();
  }, [refreshTracts]);

  useEffect(() => {
    refreshRegulatory();
  }, [refreshRegulatory]);

  // Lazy: only fan out the deal-wide data while the toggle is on "Whole deal".
  useEffect(() => {
    if (childScope === "deal") refreshDealWide();
  }, [childScope, refreshDealWide]);

  // See LeaseDeedsTab. Two reserved columns here: `well_integrated_date` is
  // owned by BD.DivisionOrders and `wegid` by the land team (the field
  // inventory marks it internal, so a BD.BrokerAgent may read it but not write
  // it). For any role that does not own one the server marks it
  // `editable_for_me: false` and it renders read-only in the grid rather than
  // as an input that 403s.
  //
  // `allocated_value` gets the harsher treatment — WIG's own allocation of the
  // purchase price across wells, so an external brokerage must not see the
  // number at all. `filterBrokerHiddenFields` drops it from the metadata
  // outright, taking it off the grid AND out of the drawer, the same way
  // LeaseDeedsTab handles `lease_cost`.
  const filteredWellMeta = useMemo(
    () =>
      filterBrokerHiddenFields(
        applyFieldEditability(wellMeta).filter(
          (m) => !HIDDEN_WELL_FIELDS.has(m.field)
        ),
        "wells",
        isBrokerAgent
      ),
    [wellMeta, isBrokerAgent]
  );
  // Row-edit gate for the wells grid. `record.edit` alone hid the ONLY route
  // to `well_integrated_date` from the role that owns it: BD.DivisionOrders
  // could see the well and never record the handoff. Falling back to the
  // metadata keeps the two in step — the form is already per-field, so a role
  // that gets in this way sees just its own column. Create/delete stay on
  // `record.edit`: stamping a date is not licence to add or destroy a well.
  //
  // `editable_for_me === true` is explicit on purpose: an older de-hub omits
  // the flag, and `editable !== false` would then admit every role.
  const mayUpdateWell = useMemo(
    () =>
      mayEditRecords ||
      filteredWellMeta.some((m) => m?.editable_for_me === true && !m.hide),
    [mayEditRecords, filteredWellMeta]
  );
  const filteredTractMeta = useMemo(
    () => (tractMeta || []).filter((m) => !HIDDEN_TRACT_FIELDS.has(m.field)),
    [tractMeta]
  );
  const filteredRegulatoryMeta = useMemo(
    () =>
      (regulatoryMeta || []).filter(
        (m) => !HIDDEN_REGULATORY_FIELDS.has(m.field)
      ),
    [regulatoryMeta]
  );

  // Injects the full-width share-bounds banner at the top of the tract form's
  // Terms section (allocation / wi / nri) — see withShareBoundsNotice for why
  // this goes through the metadata.
  const tractPanelProps = useMemo(
    () =>
      withShareBoundsNotice({
        metadata: filteredTractMeta,
        customRenderers: {
          county: countyFieldRenderer,
          // township / range / section as lookups against the survey grid —
          // the same cascade county belongs to.
          ...TRS_FIELD_RENDERERS,
          ...INTEREST_FIELD_RENDERERS,
        },
      }),
    [filteredTractMeta]
  );

  // Operators lookup fetcher — backs the `operator_id` Lookup field on
  // the wells form. The library's `lookupFetchers[name]` contract is
  // `(query) => Promise<{id, label}[]>`. The operators endpoint doesn't
  // support a server-side `q` param, so we fetch the full active list and
  // filter it here against the typeahead query. The filtering MUST happen in
  // the fetcher: the library's LookupSelect sets `filterOption={false}` and
  // treats the fetcher as the whole search, so a fetcher that ignores `query`
  // hands back the full list on every keystroke — the dropdown never narrows
  // and the `+ Add "..."` row reads as the only response to typing. Same
  // pattern as dealFormDrawer's brokers_xref / warwick_landman fetchers.
  //
  // The label appends the operator's code — `DEVON ENERGY (DEV100)` — because
  // operator names are NOT unique (de-hub migration 065; the upstream master
  // holds eight names under two codes each). Without the code the picker would
  // show two identical options with no way to choose between them. Operators
  // added by hand have no code and render as the bare name. Appending it to
  // the label also keeps it searchable, since AntD filters on label text.
  const lookupFetchers = useMemo(
    () => ({
      operators: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listOperators({
          token: tk,
          userOid,
          includeInactive: false,
        });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((o) => ({ id: o.id, label: operatorLabel(o) }))
          : [];
        // Substring match on the label, which carries the code, so `BE AN`
        // and `106856` both narrow to BE ANADARKO II LLC (106856). Spaces are
        // significant — `89 ` keeps 89 ENERGY and drops 894 OPERATING.
        return filterLookupOptions(opts, query);
      },
    }),
    [getAuth]
  );

  // Inline create-on-the-fly for the `operators` lookup (wig-react-library
  // 1.0.101+). Keyed by lookup name; the library invokes this with the text
  // the user typed and expects `Promise<{id, label}>` for the freshly created
  // option, which it then selects. Mirrors EntitiesTab's `lookupCreators` but
  // for operators.
  //
  // No `operatorCode` is sent: an operator typed in here is by definition not
  // one that came from upstream, so it has no code. That also keeps POST
  // /operators on its name-keyed idempotent path, which still prevents this
  // flow from duplicating an existing ACTIVE operator — `data.name` is the
  // authoritative label (fall back to the typed name). The label mirrors
  // `lookupFetchers` so a freshly created option reads the same as a fetched
  // one; a brand-new operator has no code, but an idempotent hit on an
  // upstream row will return one.
  const lookupCreators = useMemo(
    () => ({
      operators: async (name) => {
        const { token: tk, userOid } = await getAuth();
        let resp;
        try {
          resp = await dealsService.createOperator({
            token: tk,
            userOid,
            name,
          });
        } catch (err) {
          // 409: the name clashes with an existing operator. Fixable by
          // typing another name, so it goes on the field; anything else
          // falls to the library's toast.
          if (err?.response?.status === 409) {
            return { fieldError: apiErrorText(err) };
          }
          throw err;
        }
        const c = resp?.data?.data || resp?.data || {};
        return { id: c.id, label: operatorLabel({ ...c, name: c.name || name }) };
      },
    }),
    [getAuth]
  );

  // Grid cell renderers for the wells grid. The library passes a
  // `{ value, data, index }` params object (NOT the bare value). `operator_id`
  // is a Lookup whose stored value is an FK id, so resolve it to the operator
  // label — an unresolved id (map still loading, or the fetch failed) falls
  // back to the raw value rather than blanking the cell.
  const wellCellRenderers = useMemo(
    () => ({
      ...numericCellRenderers(filteredWellMeta),
      operator_id: ({ value }) =>
        value == null || value === "" ? "" : operatorNames[value] ?? value,
    }),
    [operatorNames, filteredWellMeta]
  );

  // Well CRUD handlers.
  const onCreateWell = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createWell({
      token: tk,
      userOid,
      // The panel renders the reserved columns disabled rather than dropping
      // them, so the body still carries  from the
      // empty date picker — and POST /wells judges every key present, which
      // 403s the create on a column the form had greyed out.
      body: { ...stripNonWritableFields(body, wellMeta), deal_id: dealId },
    });
    const created = resp?.data?.data;
    await refreshWells();
    if (created?.id) setSelectedWellId(created.id);
    return resp;
  };
  const onUpdateWell = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateWell({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(stripNonWritableFields(body, wellMeta)),
    });
    await refreshWells();
    return resp;
  };
  const onDeleteWell = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteWell({ token: tk, userOid, id });
    if (selectedWellId === id) {
      setSelectedWellId(null);
      setSelectedTractId(null);
    }
    await refreshWells();
  };

  // Well-tract CRUD — scoped to selectedWellId.
  const onCreateTract = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createWellTract({
      token: tk,
      userOid,
      body: { ...body, well_id: selectedWellId },
    });
    const created = resp?.data?.data;
    await refreshTracts();
    if (created?.id) setSelectedTractId(created.id);
    return resp;
  };
  const onUpdateTract = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateWellTract({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refreshTracts();
    return resp;
  };
  const onDeleteTract = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteWellTract({ token: tk, userOid, id });
    if (selectedTractId === id) setSelectedTractId(null);
    await refreshTracts();
  };

  // Regulatory CRUD — scoped to selectedTractId.
  const onCreateRegulatory = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createWellTractRegulatory({
      token: tk,
      userOid,
      body: { ...body, well_tract_id: selectedTractId },
    });
    await refreshRegulatory();
    return resp;
  };
  const onUpdateRegulatory = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateWellTractRegulatory({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refreshRegulatory();
    return resp;
  };
  const onDeleteRegulatory = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteWellTractRegulatory({ token: tk, userOid, id });
    await refreshRegulatory();
  };

  const wellOptions = useMemo(
    () =>
      (wells || []).map((w) => ({ value: w.id, label: wellLabel(w) })),
    [wells]
  );
  const tractOptions = useMemo(
    () =>
      (tracts || []).map((t) => ({ value: t.id, label: tractLabel(t) })),
    [tracts]
  );

  const noWell = !selectedWellId;
  const noTract = !selectedTractId;

  const placeholderCard = (entityLabel) => (
    <Card size="small">
      <Empty
        description={<Text type="secondary">{entityLabel}</Text>}
      />
    </Card>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }} className="wig-multi-table-tab">
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load wells"
          description={errMsg}
        />
      )}

      {/* View-scope toggle — pinned at the top so it governs the whole tab. */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "4px 10px",
          borderRadius: 6,
          background:
            "var(--ant-color-primary-bg-hover, rgba(22, 119, 255, 0.2))",
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          Show:
        </Text>
        <Segmented
          value={childScope}
          onChange={setChildScope}
          options={[
            { label: "Selected well", value: "selected" },
            { label: "Whole deal", value: "deal" },
          ]}
        />
      </div>

      {/* Attachments live on their own tab, scoped by parent type + entity.
          UAT testers never found that picker, so each level that can carry a
          file gets a direct route to it from here. */}
      <Card
        size="small"
        title={`Wells (${wells.length})`}
        extra={
          <AttachmentsScopeButton
            parentTable="well"
            parentId={selectedWellId}
            tooltip="Attach files to a well on this deal"
          />
        }
      >
        <WigMetadataCrudPanel
          title="Wells"
          rows={wells}
          metadata={filteredWellMeta}
          loading={loadingTop}
          allowView
          disableNonEditable
          onCreate={mayEditRecords ? onCreateWell : undefined}
          onUpdate={mayUpdateWell ? onUpdateWell : undefined}
          onDelete={mayEditRecords ? onDeleteWell : undefined}
          lookupFetchers={lookupFetchers}
          lookupCreators={lookupCreators}
          cellRenderers={wellCellRenderers}
          newRowDefaults={{ deal_id: dealId }}
          emptyText="No wells on this deal yet"
          editTrigger="button"
          editLayout="fullscreen"
          height={320}
        />
      </Card>

      <Card size="small">
        <Space align="center">
          <Text strong>Active Well:</Text>
          <Select
            value={selectedWellId}
            options={wellOptions}
            onChange={(val) => {
              setSelectedWellId(val);
              // Cascade: changing the well clears the active tract; the
              // tract effect will re-select the first tract of the new
              // well on its next fetch.
              setSelectedTractId(null);
            }}
            placeholder="Pick a well to manage its tracts"
            style={{ minWidth: 360 }}
            disabled={!wells.length}
            allowClear
          />
        </Space>
      </Card>

      {childScope === "deal" ? (
        <>
          <Card size="small" title={`Well Tracts — whole deal (${dealWellTracts.length})`}>
            <ReadOnlyChildTable
              parentHeader="Well"
              rows={dealWellTracts}
              metadata={tractMeta}
              tableName="well_tracts"
              loading={loadingDealWide}
              emptyText="No well tracts on this deal"
            />
          </Card>
          <Card size="small" title={`Regulatory — whole deal (${dealRegulatory.length})`}>
            <ReadOnlyChildTable
              parentHeader="Well Tract"
              rows={dealRegulatory}
              metadata={filteredRegulatoryMeta}
              tableName="well_tract_regulatory"
              loading={loadingDealWide}
              emptyText="No regulatory records on this deal"
            />
          </Card>
        </>
      ) : (
        <>
          {noWell ? (
            placeholderCard("Select a well above to manage its tracts.")
          ) : (
            <Card
              size="small"
              title={`Tracts for ${wellLabel(
                wells.find((w) => w.id === selectedWellId)
              )} (${tracts.length})`}
              extra={
                <AttachmentsScopeButton
                  parentTable="well_tract"
                  parentId={selectedTractId}
                  tooltip="Attach files to a well tract on this deal"
                />
              }
            >
              <WigMetadataCrudPanel
                title="Well Tracts"
                rows={tracts}
                loading={loadingTracts}
                allowView
                disableNonEditable
                onCreate={mayEditRecords ? onCreateTract : undefined}
                onUpdate={mayEditRecords ? onUpdateTract : undefined}
                onDelete={mayEditRecords ? onDeleteTract : undefined}
                cellRenderers={numericCellRenderers(filteredTractMeta)}
                // metadata / customRenderers / formFieldFilter: county cascade +
                // the 0–1 fraction guards + the section banner.
                {...tractPanelProps}
                newRowDefaults={{ well_id: selectedWellId }}
                emptyText="No tracts on this well yet"
                editTrigger="button"
                editLayout="wide"
                height={260}
              />
            </Card>
          )}

          <Card size="small">
            <Space align="center">
              <Text strong>Active Well Tract:</Text>
              <Select
                value={selectedTractId}
                options={tractOptions}
                onChange={setSelectedTractId}
                placeholder="Pick a tract to manage its regulatory rows"
                style={{ minWidth: 360 }}
                disabled={!tracts.length}
                allowClear
              />
            </Space>
          </Card>

          {noTract ? (
            placeholderCard(
              noWell
                ? "Select a well and tract above to manage regulatory rows."
                : "Select a tract above to manage its regulatory rows."
            )
          ) : (
            <Card
              size="small"
              title={`Regulatory for ${tractLabel(
                tracts.find((t) => t.id === selectedTractId)
              )} (${regulatory.length})`}
            >
              <WigMetadataCrudPanel
                title="Regulatory"
                rows={regulatory}
                metadata={filteredRegulatoryMeta}
                loading={loadingRegulatory}
                allowView
                disableNonEditable
                onCreate={mayEditRecords ? onCreateRegulatory : undefined}
                onUpdate={mayEditRecords ? onUpdateRegulatory : undefined}
                onDelete={mayEditRecords ? onDeleteRegulatory : undefined}
                newRowDefaults={{ well_tract_id: selectedTractId }}
                emptyText="No regulatory rows on this tract yet"
                editTrigger="button"
                editLayout="wide"
                height={260}
              />
            </Card>
          )}
        </>
      )}
    </Space>
  );
};

export default WellsTab;
